import React, { useEffect } from 'react';
import {
  reduxForm,
  InjectedFormProps,
  Field,
  WrappedFieldProps,
} from 'redux-form';
import { Link, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, TextField } from '@mui/material';
import { required } from '../../../components/validation/ValidationUtils';

export interface LoginFormProps {
  isError?: boolean;
  isFetching?: boolean;
  loggedOut?: boolean;
  expired?: boolean;
  initialValues?: LoginFormModel;
}

export interface LoginFormModel {
  email: string;
  password: string;
}

const renderTextField: React.FC<
  WrappedFieldProps & {
    label: string;
    type?: string;
    margin?: 'none' | 'dense' | 'normal';
    fullWidth?: boolean;
    InputLabelProps?: any;
  }
> = ({ label, input, meta: { touched, invalid, error }, ...custom }) => (
  <TextField
    {...input}
    {...custom}
    label={label}
    error={touched && invalid}
    helperText={touched && error}
  />
);

type ComponentProps = LoginFormProps &
  InjectedFormProps<LoginFormModel, LoginFormProps>;

const TypedField = Field as unknown as React.FC<any>;

const LoginFormComponent: React.FC<ComponentProps> = ({
  handleSubmit,
  isError,
  isFetching,
  loggedOut,
  expired,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'appName' });
  }, [intl]);

  return (
    <div className="body-inner">
      <div className="card bg-color-light">
        <div className="card-content theme-light">
          <section className="logo text-center">
            <h1>{intl.formatMessage({ id: 'appName' })}</h1>
          </section>

          <form className="form-horizontal" onSubmit={handleSubmit}>
            <input style={{ display: 'none' }} type="text" name="email" />
            <input
              style={{ display: 'none' }}
              type="password"
              name="password"
            />

            <fieldset>
              <div className="form-group">
                <TypedField
                  component={renderTextField}
                  label={intl.formatMessage({ id: 'user.name' })}
                  name="email"
                  margin="normal"
                  validate={required}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <div className="form-group">
                <TypedField
                  component={renderTextField}
                  label={intl.formatMessage({ id: 'password' })}
                  type="password"
                  name="password"
                  margin="normal"
                  validate={required}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </fieldset>

            <div className="card-action no-border text-right">
              <Button
                style={{ marginRight: '6.2rem', fontSize: '1.05rem' }}
                color="secondary"
                onClick={() => navigate('/requestResetPassword')}
              >
                {intl.formatMessage({ id: 'forgotPassword' })}
              </Button>
              <Button type="submit" color="primary" disabled={isFetching}>
                {isFetching
                  ? intl.formatMessage({ id: 'pleaseWait' })
                  : intl.formatMessage({ id: 'login' })}
              </Button>
            </div>
          </form>

          {isError && (
            <div className="callout callout-danger no-margin-bottom">
              <p>{intl.formatMessage({ id: 'loginError' })}</p>
            </div>
          )}
          {loggedOut && (
            <div className="callout callout-success no-margin-bottom">
              <p>{intl.formatMessage({ id: 'loggedOut' })}</p>
            </div>
          )}
          {expired && (
            <div className="callout callout-warning no-margin-bottom">
              <p>{intl.formatMessage({ id: 'sessionExpired' })}</p>
            </div>
          )}
        </div>
      </div>

      <div className="additional-info text-center" style={{ marginTop: 16 }}>
        <Link
          to="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '0.9rem' }}
        >
          {intl.formatMessage({ id: 'privacyPolicy.page.title' })}
        </Link>
      </div>
    </div>
  );
};

const LoginForm = reduxForm<LoginFormModel, LoginFormProps>({
  form: 'LoginForm',
})(LoginFormComponent) as unknown as React.ComponentType<LoginFormProps>;
export { renderTextField };
export default LoginForm;
